import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getProduct } from "@/lib/api";
import { ArrowRight, ShieldCheck, MapPin, CalendarDays, PackageCheck, Star, Award, Droplet, Heart, Zap, Leaf, Sparkles, ScrollText } from "lucide-react";

const SWEET_LIME_IMG = "/photos-r3b/SPOT-07-main-background.jpg";
const SWEET_LIME_GALLERY = [
  "/photos-r3b/real-harvest-bucket.jpg",           // pos 1 — kept per brief
  "/photos-r4/gallery-02-hand-cluster.jpg",        // pos 2 — Round 4 new
  "/photos-r4/gallery-03-jalna-gold-bag.png",      // pos 3 — Round 4 new
  "/photos-r4/gallery-04-package-trio.png",        // pos 4 — Round 4 new
  "/photos/IMG-20260723-WA0011.jpg",               // pos 5 — kept per brief
  "/photos-r4/gallery-06-ripening-fruit.jpg",      // pos 6 — Round 4 new
];

export default function ProductDetail() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setP(null); setErr(null);
    getProduct(slug).then(setP).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32" data-testid="product-not-found">
        <h1 className="tr-section-title text-4xl">{t("product_detail.not_found_title")}</h1>
        <p className="mt-4 text-[var(--tr-muted)]">{err}</p>
        <Link to="/" className="tr-pill-btn tr-btn-primary mt-6">{t("common.back_home")}</Link>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-32 animate-pulse">
        <div className="h-8 w-40 bg-[var(--tr-sage)] rounded-full mb-6" />
        <div className="h-16 w-3/4 bg-[var(--tr-sage)] rounded-xl" />
        <div className="mt-10 h-96 w-full bg-[var(--tr-sage)] rounded-3xl" />
      </div>
    );
  }

  const isSweetLime = p.slug === "jalna-sweet-lime-mosambi";
  const heroImg = isSweetLime ? SWEET_LIME_IMG : p.image_url;
  const galleryImgs = isSweetLime ? SWEET_LIME_GALLERY : (p.gallery || []);

  const meta = [
    { icon: MapPin, label: t("product_detail.origin"), value: p.origin },
    { icon: CalendarDays, label: t("product_detail.seasonality"), value: p.seasonality },
    { icon: PackageCheck, label: t("product_detail.packaging"), value: p.packaging },
    { icon: ShieldCheck, label: t("product_detail.compliance"), value: (p.certifications || []).join(" · ") },
  ];

  return (
    <div data-testid={`page-product-${p.slug}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--tr-deep-green)] via-[var(--tr-deep-green)] to-[#0a5a44] tr-grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center text-white">
          <div className="lg:col-span-6">
            <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{p.category}</div>
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl mt-4 tracking-tight leading-[0.95]">{p.name}</h1>
            {p.tagline && <p className="mt-5 text-xl text-white/85 leading-relaxed max-w-xl">{p.tagline}</p>}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/buyers" data-testid="product-enquire-cta" className="tr-pill-btn tr-btn-zest">
                {t("product_detail.enquire")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="product-contact-cta" className="tr-pill-btn bg-white/15 text-white border border-white/25 hover:bg-white/25">
                {t("product_detail.talk")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="rounded-3xl overflow-hidden aspect-square border border-white/20 shadow-2xl relative">
              <img src={heroImg} alt={p.name} className="w-full h-full object-cover" />
              {/* Round 4 §1 — Separate the GI badge (top-right) from the Juice/Brix pills
                  (bottom-right) so they never overlap visually. Style stays consistent. */}
              {isSweetLime && (
                <>
                  <div data-testid="product-info-tags" className="absolute top-4 right-4 flex flex-col items-end gap-2 max-w-[70%]">
                    <span data-testid="product-info-tag-gi" className="inline-flex items-center gap-2 rounded-full bg-[var(--tr-citrus-yellow)] text-[var(--tr-deep-green)] px-3 py-1.5 shadow-lg border border-white/60 text-xs font-extrabold uppercase tracking-[0.14em]">
                      <Award className="w-3.5 h-3.5" /> GI No. 495 · GI Recognised
                    </span>
                  </div>
                  <div data-testid="product-info-tags-secondary" className="absolute bottom-4 right-4 flex flex-col items-end gap-2 max-w-[70%]">
                    <span data-testid="product-info-tag-juice" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tr-deep-green)] px-3 py-1.5 shadow-lg border border-white/60 text-xs font-extrabold uppercase tracking-[0.14em]">
                      <Droplet className="w-3.5 h-3.5 text-[var(--tr-zest)]" /> Juice 45–52%
                    </span>
                    <span data-testid="product-info-tag-brix" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tr-deep-green)] px-3 py-1.5 shadow-lg border border-white/60 text-xs font-extrabold uppercase tracking-[0.14em]">
                      <Sparkles className="w-3.5 h-3.5 text-[var(--tr-citrus-yellow)]" /> Brix 9–11°
                    </span>
                  </div>
                </>
              )}
            </div>
            {/* Round 4 §1 — On Sweet Lime, dedicated info pills already carry GI/Juice/Brix,
                so we hide the p.highlights floating cards to avoid a visual duplicate overlap. */}
            {!isSweetLime && p.highlights?.slice(0, 2).map((h, i) => (
              <div key={i} className={`absolute w-56 rounded-2xl bg-white p-4 shadow-2xl text-[var(--tr-deep-green)] ${i === 0 ? "-left-6 top-10" : "-right-6 bottom-10"}`}>
                <Star className="w-4 h-4 text-[var(--tr-zest)]" />
                <div className="mt-2 font-display font-bold text-sm">{h}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {meta.map((m) => (
          <div key={m.label} className="tr-card p-6">
            <m.icon className="w-5 h-5 text-[var(--tr-zest)]" />
            <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-[var(--tr-muted)] font-bold">{m.label}</div>
            <div className="mt-2 font-display font-bold text-[var(--tr-deep-green)] leading-snug">{m.value || "—"}</div>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-6 text-lg text-[var(--tr-muted)] leading-relaxed">
          <div className="tr-eyebrow">{t("product_detail.story")}</div>
          <p className="text-2xl text-[var(--tr-deep-green)] font-display font-bold leading-snug">{p.short_description}</p>
          <p>{p.long_description}</p>
        </div>
        <aside className="lg:col-span-5">
          <div className="tr-card p-8">
            <div className="tr-eyebrow">{t("product_detail.highlights")}</div>
            <ul className="mt-6 space-y-4">
              {(p.highlights || []).map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--tr-deep-green)]">
                  <span className="tr-dot mt-2" />
                  <span className="font-semibold">{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-[rgba(6,78,59,0.1)]">
              <div className="tr-eyebrow">{t("product_detail.gi_recognised_label")}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {(p.certifications || []).map((c) => (
                  <span key={c} data-testid={`cert-current-${c.slice(0,10)}`} className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[var(--tr-lime)]/40 text-[var(--tr-deep-green)] border border-[var(--tr-deep-green)]/15">{c}</span>
                ))}
              </div>
              {(p.aspirational_certifications || []).length > 0 && (
                <>
                  <div className="tr-eyebrow mt-6 !text-[var(--tr-zest)] before:!bg-[var(--tr-zest)]">In progress · Aspirational</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.aspirational_certifications.map((c) => (
                      <span key={c} data-testid={`cert-asp-${c.slice(0,10)}`} className="px-3 py-1.5 text-xs font-semibold rounded-full bg-white border border-dashed border-[var(--tr-zest)] text-[var(--tr-zest)]">{c}</span>
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] text-[var(--tr-muted)] italic leading-relaxed">These are current work-in-progress commitments; not yet certified statuses.</p>
                </>
              )}
            </div>
          </div>
        </aside>
      </section>

      {isSweetLime && (
        <>
          {/* HIGH Sweet-Lime Item 1 — story / history alongside GI */}
          <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="sweet-lime-story">
            <div className="lg:col-span-5">
              <div className="tr-eyebrow">{t("product_detail.story_history_eyebrow")}</div>
              <h2 className="tr-section-title text-3xl md:text-5xl mt-4">{t("product_detail.story_history_title")}</h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-[var(--tr-muted)] leading-relaxed">
              <p>{t("product_detail.story_p1")}</p>
              <p>{t("product_detail.story_p2")}</p>
              <p>{t("product_detail.story_p3")}</p>
              <div className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-[var(--tr-lime)]/30 border border-[var(--tr-deep-green)]/15 px-5 py-3">
                <ScrollText className="w-5 h-5 text-[var(--tr-deep-green)]" />
                <span className="text-sm font-semibold text-[var(--tr-deep-green)]">{t("product_detail.gi_registered_year")}</span>
              </div>
            </div>
          </section>

          {/* MEDIUM Sweet-Lime Item 2 — nutrition infographic */}
          <section className="bg-[var(--tr-sage)]/40 border-y border-[rgba(6,78,59,0.08)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="sweet-lime-nutrition">
              <div className="max-w-3xl">
                <div className="tr-eyebrow">{t("product_detail.nutrition_eyebrow")}</div>
                <h2 className="tr-section-title text-3xl md:text-4xl mt-4">{t("product_detail.nutrition_title")}</h2>
                <p className="mt-4 text-[var(--tr-muted)] leading-relaxed">{t("product_detail.nutrition_body")}</p>
              </div>
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
                {[
                  { icon: Droplet, k: "juice", color: "text-[var(--tr-zest)]" },
                  { icon: Sparkles, k: "vitc", color: "text-[var(--tr-citrus-yellow)]" },
                  { icon: Heart, k: "heart", color: "text-red-500" },
                  { icon: Zap, k: "energy", color: "text-amber-500" },
                ].map((n) => (
                  <div key={n.k} className="tr-card p-5 text-center" data-testid={`nutrition-${n.k}`}>
                    <n.icon className={`w-10 h-10 mx-auto ${n.color}`} strokeWidth={1.5} />
                    <div className="mt-3 font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t(`product_detail.nut_${n.k}_v`)}</div>
                    <div className="mt-1 text-sm font-semibold text-[var(--tr-deep-green)]">{t(`product_detail.nut_${n.k}_t`)}</div>
                    <div className="mt-2 text-xs text-[var(--tr-muted)] leading-relaxed">{t(`product_detail.nut_${n.k}_b`)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3">
                {[
                  { k: "retail", icon: Leaf },
                  { k: "horeca", icon: Leaf },
                  { k: "processing", icon: Leaf },
                  { k: "export", icon: Leaf },
                ].map((s) => (
                  <div key={s.k} className="flex items-start gap-3 rounded-2xl bg-white border border-[rgba(6,78,59,0.08)] p-4" data-testid={`nut-segment-${s.k}`}>
                    <s.icon className="w-4 h-4 text-[var(--tr-lime)] mt-1" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--tr-zest)] font-bold">{t(`product_detail.nut_seg_${s.k}_t`)}</div>
                      <div className="mt-1 text-xs text-[var(--tr-muted)] leading-relaxed">{t(`product_detail.nut_seg_${s.k}_b`)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-[11px] italic text-[var(--tr-muted)] leading-relaxed">{t("product_detail.nutrition_disclaimer")}</p>
            </div>
          </section>
        </>
      )}

      {galleryImgs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="product-gallery">
          <div className="tr-eyebrow mb-6">{t("product_detail.gallery")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {galleryImgs.map((g, i) => (
              <div key={i} className="rounded-3xl overflow-hidden aspect-[4/5] border border-[rgba(6,78,59,0.1)]">
                <img src={g} alt={`${p.name} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
